import React, { useState } from 'react';
import { RotateCw } from 'lucide-react';
import Button from '../ui/Button';
import { newEditId, rotatePhoto, type EquipmentTargetKind } from '../../api/equipmentEdit';
import { nextRotation } from '../LabelPhotoImage';
import { extractErrorMessage } from '../../utils/extractErrorMessage';

/** FS-1079 — Rotate's hover help, for an operator who has never seen it. */
export const ROTATE_TITLE =
  'Turn this photo a quarter turn clockwise and save it, so the label reads upright. ' +
  'It shows this way everywhere the console and the app draw it. The photo file itself is not changed.';

export interface RotatePhotoButtonProps {
  serial: string;
  url: string;
  /** The record the photo is drawn on. The turn is saved on every record that carries the photo. */
  target: { kind: EquipmentTargetKind; path: string };
  /** The turn drawn now. */
  degrees: number;
  /** Why Rotate cannot be pressed right now, or null when it can. */
  blockedTitle?: string | null;
  /** Label photo number, for the accessible name. */
  index: number;
  onRotated: (degrees: number) => void;
  /** Tells the drawer a write is running, so its other writes wait. */
  onWorking?: (working: boolean) => void;
}

/**
 * FS-1079 — turns a label photo a quarter turn clockwise per press and saves
 * it at once: no confirmation, because pressing again turns it on, and the
 * file is never changed.
 */
const RotatePhotoButton: React.FC<RotatePhotoButtonProps> = ({
  serial,
  url,
  target,
  degrees,
  blockedTitle = null,
  index,
  onRotated,
  onWorking,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rotate = async () => {
    const next = nextRotation(degrees);
    setBusy(true);
    setError(null);
    onWorking?.(true);
    try {
      const result = await rotatePhoto(serial, { edit_id: newEditId(), url, degrees: next, target });
      onRotated(result.degrees);
    } catch (err) {
      setError(`Couldn't rotate the photo: ${extractErrorMessage(err)}`);
    } finally {
      setBusy(false);
      onWorking?.(false);
    }
  };

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        disabled={busy || blockedTitle !== null}
        onClick={() => void rotate()}
        aria-label={`Rotate label photo ${index + 1}`}
        data-testid="rotate-photo"
        title={blockedTitle ?? (busy ? 'Saving the turn…' : ROTATE_TITLE)}
        className="mt-0.5"
      >
        <RotateCw className="h-3 w-3" aria-hidden="true" />
        Rotate
      </Button>
      {error && (
        <p role="alert" className="mt-0.5 text-xs leading-tight text-status-error-foreground">
          {error}
        </p>
      )}
    </>
  );
};

export default RotatePhotoButton;
